import React, { useState } from 'react';
import { Button, Card, CardContent, Divider, Grid } from '@mui/material';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import AdminService, { UpdatePasswordProps } from 'services/hooks/admin-service';
import useUpdatePassword from './crud/useUpdatePassword';
import InputField from 'components/elements/Form/Input/InputField';
import PrimaryButton from 'components/elements/Form/button/primaryButton';
import { toast } from 'react-toastify';
import { generateUpdateErrorMessage } from 'utils/helpers';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import { colours } from 'utils/constants';

/* The code is defining a validation schema using the `yup` library. If these validation rules are not met, an error message will be displayed. */
const UpdatePasswordSchema = yup.object().shape({
  currentPassword: yup.string().required('Current password is required'),
  newPassword: yup
    .string()
    .required('New password is required')
    .min(8, 'Must be at least 8 characters')
    .matches(/[A-Z]/, 'Must include at least one uppercase letter')
    .matches(/[a-z]/, 'Must include at least one lowercase letter')
    .matches(/[0-9]/, 'Must include at least one number'),
  retypePassword: yup
    .string()
    .required('Please retype your new password')
    .oneOf([yup.ref('newPassword')], 'New passwords must match')
});

const ResetPasswordForm = ({ onBack, editUUID }: { onBack: () => void; editUUID: string }) => {
  const [disabled, setDisabled] = useState(false);

  /* The `useForm` hook from the `react-hook-form` library is being used to create a form with form validation. */
  const methods = useForm({
    defaultValues: {
      currentPassword: '',
      newPassword: '',
      retypePassword: ''
    },
    resolver: yupResolver(UpdatePasswordSchema)
  });

  /* The code is creating a mutation function called `updatePasswordMutation` using the `useUpdatePassword` hook. This hook takes in 
  several parameters: */
  const updatePasswordMutation = useUpdatePassword({
    updateQuery: AdminService.updatePassword,
    queryName: 'updatepassword',
    onSuccess: () => {
      onBack();
      toast.success('Password updated successfully!');
      setDisabled(false);
    },
    onError: (error: any) => {
      if (error.response && error.response.data && error.response.data.error) {
        if (error.response.data.error === 'Provided current password is wrong') {
          toast.error(
            'The entered password does not match our records. Please double-check your password and try again.'
          );
        } else {
          toast.error(generateUpdateErrorMessage('Password'));
        }
      } else {
        toast.error(
          'Something went wrong. Please try again or contact support if the issue persists.'
        );
      }
      setDisabled(false);
    }
  });

  /* The `onSubmit` function takes an `UpdatePasswordProps` object as an argument, extracts specific property from it, and passes them to the 
  `updatePasswordMutation.mutate` function. */
  const onSubmit = async (e: UpdatePasswordProps) => {
    setDisabled(true);
    if (!!editUUID) {
      updatePasswordMutation.mutate({
        uuid: editUUID,
        currentPassword: e?.currentPassword,
        newPassword: e?.newPassword
      });
    }
  };

  return (
    <form onSubmit={methods.handleSubmit(onSubmit)} className={`text-left items-start`}>
      <div>
        <Card variant="outlined" sx={{ width: '100%', borderRadius: '8px' }}>
          <CardContent>
            <Grid item xs={1}>
              <Button
                color="primary"
                fullWidth
                onClick={onBack}
                sx={{
                  justifyContent: 'flex-start',
                  textTransform: 'none',
                  color: 'black',
                  fontSize: '1rem'
                }}
              >
                <ArrowBackIosIcon sx={{ width: '15px', color: colours.seafoam }} />
                Back
              </Button>
            </Grid>
            <Divider variant="middle" style={{ margin: '20px 0 40px' }} />
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <InputField
                  label="Type your current password*"
                  name="currentPassword"
                  type="password"
                  placeholder="**********"
                  autoFocus
                  methods={methods}
                  autoComplete="new-password"
                />
              </Grid>
              <Grid item xs={12}>
                <InputField
                  label="Type your new password*"
                  name="newPassword"
                  type="password"
                  placeholder="**********"
                  autoFocus
                  methods={methods}
                  autoComplete="new-password"
                />
              </Grid>
              <Grid item xs={12}>
                <InputField
                  label="Retype your new password*"
                  name="retypePassword"
                  type="password"
                  placeholder="**********"
                  autoFocus
                  methods={methods}
                  autoComplete="new-password"
                />
              </Grid>
              <Grid item xs={4}>
                <PrimaryButton
                  buttonLabel="Save Password"
                  variant="default"
                  size={'mt-4 px-6 w-auto gap-2'}
                  disabled={disabled}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </div>
    </form>
  );
};

export default ResetPasswordForm;
